import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, concatMap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';

import { Product, ProductCountry } from '@app/models';
import { ApiService } from '@app/services/api/api.service';
import {
  LoadByProduct,
  LoadByProductFail,
  LoadByProductSuccess,
  LoadByFollow,
  LoadByFollowFail,
  LoadByFollowSuccess,
  ProductCountryActionTypes,
  Update,
  UpdateSuccess,
  UpdateFail,
  UpdateOne,
  BulkUpdate,
  BulkUpdateSuccess,
  BulkUpdateFail,
  UpdateMany,
  IterableUpdate,
  LoadByProductMirror,
  LoadByProductMirrorSuccess,
  LoadByProductMirrorFail
} from '@core/actions/product-country.actions';
import { FlashService } from '@app/services/flash.service';
import { componentRefresh } from '@angular/core/src/render3/instructions';

@Injectable()
export class ProductCountryEffects {
  @Effect()
  loadByProduct$: Observable<Action> = this.actions$.pipe(
    ofType<LoadByProduct>(ProductCountryActionTypes.LoadByProduct),
    map(action => action.id),
    switchMap(id =>
      this.api.getProductCountries(id).pipe(
        map(
          (countries: ProductCountry[]) => new LoadByProductSuccess(countries)
        ),
        catchError(error => of(new LoadByProductFail(error)))
      )
    )
  );

  @Effect()
  loadByFollow$: Observable<Action> = this.actions$.pipe(
    ofType<LoadByFollow>(ProductCountryActionTypes.LoadByFollow),
    map(action => [
      action.followId,
      action.countryId,
      action.countryDepartmentId,
      action.CountryBuyerId,
      action.businessUnit,
      action.typeHierarchy,
      action.DestinationCenterId,
      action.component
    ]),
    switchMap(
      ([
        followId,
        countryId,
        countryDepartmentId,
        CountryBuyerId,
        businessUnit,
        typeHierarchy,
        DestinationCenterId,
        component
      ]) =>
        this.api
          .getProductCountriesByFollow(
            followId,
            countryId,
            countryDepartmentId,
            CountryBuyerId,
            businessUnit,
            typeHierarchy,
            DestinationCenterId,
            component
          )
          .pipe(
            map(
              (countries: ProductCountry[]) =>
                new LoadByFollowSuccess(countries)
            ),
            catchError(error => of(new LoadByFollowFail(error)))
          )
    )
  );

  @Effect()
  update$: Observable<Action> = this.actions$.pipe(
    ofType<Update>(ProductCountryActionTypes.Update),
    map(action => [action.id, action.data]),
    switchMap(([id, data]) => {
      return this.api.updateProductCountry(id, data).pipe(
        map(
          (productCountry: ProductCountry) => new UpdateSuccess(productCountry)
        ),
        catchError(error => of(new UpdateFail(error)))
      );
    })
  );

  @Effect()
  updateSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateSuccess>(ProductCountryActionTypes.UpdateSuccess),
    map(action => new UpdateOne(action.productCountry))
  );

  // @Effect({ dispatch: false })
  // iterableUpdate$ = this.actions$.pipe(
  //   ofType<IterableUpdate>(ProductCountryActionTypes.IterableUpdate),
  //   map(async action => {
  //     await action.iterableData.map(async data => {
  //       console.log('sadasdasdasd', action);
  //       return this.api
  //         .bulkUpdateProductCountry(
  //           action.ProductIds,
  //           action.dataProduct.CountryId,
  //           action.dataProduct.businessUnit,
  //           data
  //         )
  //         .pipe(
  //           map(async (productCountries: ProductCountry[]) => {
  //             console.log('bulkUpdate');
  //             const message =
  //               action.ProductIds.length > 1
  //                 ? `${action.ProductIds.length} Productos actualizados`
  //                 : 'Producto actualizado';
  //             this.fs.emit({ type: 'success', message });
  //             return new BulkUpdateSuccess(productCountries);
  //           }),
  //           catchError(error => of(new BulkUpdateFail(error)))
  //         );
  //     }).toPromise;
  //   })
  // );

  @Effect()
  bulkUpdate$: Observable<Action> = this.actions$.pipe(
    ofType<BulkUpdate>(ProductCountryActionTypes.BulkUpdate),
    map(action => [
      action.ProductIds,
      action.CountryId,
      action.businessUnit,
      action.data,
      action.DestinationCenterId
    ]),
    concatMap(
      ([ProductIds, CountryId, businessUnit, data, DestinationCenterId]) => {
        return this.api
          .bulkUpdateProductCountry(
            ProductIds,
            CountryId,
            businessUnit,
            data,
            DestinationCenterId
          )
          .pipe(
            map((productCountries: ProductCountry[]) => {
              const message =
                ProductIds.length > 1
                  ? `${ProductIds.length} Productos actualizados`
                  : 'Producto actualizado';
              this.fs.emit({ type: 'success', message });
              return new BulkUpdateSuccess(productCountries);
            }),
            catchError(error => of(new BulkUpdateFail(error)))
          );
      }
    )
  );

  @Effect()
  bulkUpdateSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<BulkUpdateSuccess>(ProductCountryActionTypes.BulkUpdateSuccess),
    map(action => new UpdateMany(action.productCountries))
  );

  @Effect()
  loadByProductMirror$: Observable<Action> = this.actions$.pipe(
    ofType<LoadByProductMirror>(ProductCountryActionTypes.LoadByProductMirror),
    map(action => action.ProductId),
    switchMap(ProductId =>
      this.api.getProductCountryMirrors(ProductId).pipe(
        map(
          (ProductCountryMirror: ProductCountry[]) =>
            new LoadByProductMirrorSuccess(ProductCountryMirror)
        ),
        catchError(error => of(new LoadByProductFail(error)))
      )
    )
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions,
    private api: ApiService,
    private fs: FlashService
  ) {}
}
